import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { getContentBySlug, getPublishedContent } from '../../services/contentService';
import { BlogLayout } from '../../components/RadianceBlog';
import { transformPostToMeta, getSmartProductRecommendations, fetchSmartProductRecommendations } from '../../utils/blogHelpers';
import { useData } from '../../context/DataContext';

// Sample blog posts data for fallback
const sampleBlogPosts = [
  {
    id: 1,
    title: "The Ultimate Guide to Skincare Routines for Every Skin Type",
    slug: "ultimate-guide-skincare-routines",
    excerpt: "Discover the perfect skincare routine for your skin type with our comprehensive guide covering everything from cleansing to moisturizing.",
    image: "/images/hero-face.png",
    date: "2024-01-15",
    readTime: "8 min read",
    category: "Skincare",
    tags: ["skincare", "routine", "beauty"],
    author: "Dr. Sarah Chen",
    content: `
      <h2>Understanding Your Skin Type</h2>
      <p>Before diving into any skincare routine, it's crucial to understand your skin type. This foundational knowledge will guide every product choice and routine decision you make.</p>
      
      <h3>Common Skin Types</h3>
      <ul>
        <li><strong>Oily Skin:</strong> Characterized by excess sebum production, enlarged pores, and a tendency toward acne</li>
        <li><strong>Dry Skin:</strong> Lacks moisture, often feels tight, and may show fine lines more easily</li>
        <li><strong>Combination Skin:</strong> Oily in the T-zone (forehead, nose, chin) but normal to dry elsewhere</li>
        <li><strong>Sensitive Skin:</strong> Reacts easily to products, may experience redness, irritation, or burning</li>
        <li><strong>Normal Skin:</strong> Well-balanced, not too oily or dry, with minimal imperfections</li>
      </ul>
      
      <h2>Building Your Perfect Routine</h2>
      <p>A good skincare routine doesn't have to be complicated. In fact, simplicity often yields the best results.</p>
    `
  }
];

const BlogPostPage = () => {
  const { slug } = useParams();
  const { categories } = useData();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        // First try to fetch from API
        try {
          const response = await getContentBySlug(slug);
          if (response.success && response.data) {
            setPost(response.data);
            
            // Fetch product recommendations asynchronously
            const products = await fetchSmartProductRecommendations(response.data, categories);
            setRecommendedProducts(products);
            return;
          }
        } catch (apiError) {
          console.log('API fetch failed, trying sample data:', apiError);
        }
        
        // If API fails, try to find in sample data
        const samplePost = sampleBlogPosts.find(post => post.slug === slug);
        if (samplePost) {
          setPost(samplePost);
          // Try to fetch products for sample post too
          const products = await fetchSmartProductRecommendations(samplePost, categories);
          setRecommendedProducts(products);
        } else {
          setError('Post not found');
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load post');
      } finally {
        setLoading(false);
      }
    };

    if (slug && categories.length > 0) {
      fetchPost();
    }
  }, [slug, categories]);

  // Fetch related blog posts
  useEffect(() => {
    const fetchRelatedPosts = async () => {
      try {
        const response = await getPublishedContent('blog');
        if (response.success && response.data) {
          // Filter out the current post and get up to 3 related posts
          const related = response.data
            .filter(item => item.slug !== slug)
            .slice(0, 3)
            .map(item => ({
              title: item.title,
              href: `/blog/${item.slug}`,
              image: item.image || '/images/hero-face.png' // fallback image
            }));
          setRelatedPosts(related);
        }
      } catch (err) {
        console.error('Error fetching related posts:', err);
        // Don't set error state - just fail silently for related posts
      }
    };

    if (slug) {
      fetchRelatedPosts();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f8f8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ab3d22] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Transform post data to BlogMeta format using helper
  const meta = transformPostToMeta(post);

  return (
    <BlogLayout meta={meta} products={recommendedProducts} relatedPosts={relatedPosts}>
      <div dangerouslySetInnerHTML={{ __html: post.content }} />
    </BlogLayout>
  );
};

export default BlogPostPage;
